import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Avatar, Box, Chip, IconButton, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import {
  MdShoppingBag,
  MdCurrencyRupee,
  MdTrendingUp,
  MdReceiptLong,
  MdPendingActions,
  MdPersonAdd,
  MdAssignmentReturn,
  MdSupportAgent,
  MdRefresh,
  MdChevronRight,
  MdCheckCircle,
} from 'react-icons/md';
import { getData } from '../Webservice/Webservice';
import { FONT, StatCard } from './Reports/reportShared';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inrShort = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return inr(v);
};
const delta = (pct, label) => `${pct >= 0 ? '▲' : '▼'} ${Math.abs(pct)}% ${label}`;
const timeAgo = (d) => {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 22, fontSize: 11.5 });

const ORDER_STATUS = {
  pending: ['#fef3c7', '#92400e'], placed: ['#e0e7ff', '#3730a3'], picking: ['#e0f2fe', '#075985'], packed: ['#cffafe', '#155e75'],
  'ready-for-dispatch': ['#ccfbf1', '#115e59'], dispatched: ['#ede9fe', '#5b21b6'], 'in-transit': ['#f3e8ff', '#6b21a8'],
  'out-for-delivery': ['#fce7f3', '#9d174d'], delivered: ['#dcfce7', '#166534'], cancelled: ['#fee2e2', '#991b1b'], returned: ['#ffedd5', '#9a3412'], failed: ['#f1f5f9', '#475569'],
};
const PAY_STATUS = { paid: ['#dcfce7', '#166534'], pending: ['#fef3c7', '#92400e'], processing: ['#e0f2fe', '#075985'], failed: ['#fee2e2', '#991b1b'] };
const label = (s) => (s ? s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-');
const SEVERITY = { high: '#dc2626', medium: '#f59e0b', low: '#3b82f6' };
const DONUT_COLORS = ['#f59e0b', '#0ea5e9', '#8b5cf6', '#10b981', '#ef4444', '#f97316'];

const Panel = ({ title, accent = '#6366f1', action, onAction, children, sx }) => (
  <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, px: 2.5, pt: 3.5, pb: 2.5, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', ...sx }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
      <Typography sx={{ fontWeight: 800, fontSize: 14.5, fontFamily: FONT, color: '#0f172a' }}>{title}</Typography>
      {action && (
        <Box onClick={onAction} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: accent, fontSize: 11.5, fontWeight: 700, fontFamily: FONT, '&:hover': { textDecoration: 'underline' } }}>
          {action}
          <MdChevronRight size={16} />
        </Box>
      )}
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, width: '100%' }}>{children}</Box>
  </Paper>
);

const Empty = ({ text }) => (
  <Box sx={{ height: '100%', minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 11.5, fontFamily: FONT, textAlign: 'center' }}>{text}</Box>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const raw = sessionStorage.getItem('token');
      const res = await getData('dashboard/overview', null, raw ? `Bearer ${raw}` : '', false);
      if (res?.success) {
        setData(res);
        setError('');
      } else setError(res?.message || 'Failed to load dashboard');
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), 60000);
    return () => clearInterval(id);
  }, [load]);

  const k = data?.kpis;
  const go = (path) => () => navigate(path);

  const ymd = (d) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const todayStr = ymd(new Date());
  const monthStartStr = `${todayStr.slice(0, 8)}01`;
  const cards = k
    ? [
        { label: "Today's Orders", value: k.today.orders.toLocaleString('en-IN'), icon: MdShoppingBag, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: delta(k.todayChange.orders, 'vs yesterday'), onClick: go(`/ordermanagement?statusGroup=live&from=${todayStr}&to=${todayStr}`) },
        { label: "Today's Sales", value: inrShort(k.today.revenue), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: delta(k.todayChange.revenue, 'vs yesterday'), onClick: go(`/ordermanagement?statusGroup=live&from=${todayStr}&to=${todayStr}`) },
        { label: 'Sales This Month', value: inrShort(k.month.revenue), icon: MdTrendingUp, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: delta(k.month.revenueChange, 'vs same days last month'), onClick: go(`/ordermanagement?statusGroup=live&from=${monthStartStr}&to=${todayStr}`) },
        { label: 'Avg Order Value', value: inr(k.month.aov), icon: MdReceiptLong, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: `${k.month.orders.toLocaleString('en-IN')} orders this month` },
        { label: 'To Ship', value: k.toProcess.count.toLocaleString('en-IN'), icon: MdPendingActions, gradient: k.toProcess.stale ? 'linear-gradient(135deg,#f43f5e,#dc2626)' : 'linear-gradient(135deg,#14b8a6,#0d9488)', sub: k.toProcess.stale ? `${k.toProcess.stale} waiting over 2 days` : 'Nothing overdue', onClick: go('/ordermanagement?statusGroup=toship') },
        { label: 'New Customers', value: k.customers.newThisMonth.toLocaleString('en-IN'), icon: MdPersonAdd, gradient: 'linear-gradient(135deg,#ec4899,#db2777)', sub: `${k.customers.total.toLocaleString('en-IN')} customers in total`, onClick: go('/reports/customers') },
        { label: 'Returns in Progress', value: k.returns.open.toLocaleString('en-IN'), icon: MdAssignmentReturn, gradient: 'linear-gradient(135deg,#f97316,#c2410c)', sub: `${k.returns.refundPending} refunds pending`, onClick: go('/reports/returns') },
        { label: 'Open Tickets', value: (k.tickets.open + k.tickets.inProgress).toLocaleString('en-IN'), icon: MdSupportAgent, gradient: 'linear-gradient(135deg,#0f172a,#334155)', sub: `${k.tickets.open} not picked up yet`, onClick: go('/queries?status=active') },
      ]
    : [];

  const trend = useMemo(() => data?.trend || [], [data]);
  const trendOptions = useMemo(
    () => ({
      chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false }, fontFamily: FONT, parentHeightOffset: 0 },
      stroke: { width: [3, 0], curve: 'smooth' },
      fill: { type: ['gradient', 'solid'], gradient: { shade: 'light', opacityFrom: 0.45, opacityTo: 0.04, stops: [0, 100] } },
      colors: ['#6366f1', '#f59e0b'],
      dataLabels: { enabled: false },
      plotOptions: { bar: { columnWidth: '45%', borderRadius: 2 } },
      xaxis: {
        categories: trend.map((t) => new Date(`${t.date}T00:00:00Z`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'UTC' })),
        tickAmount: 10,
        axisTicks: { show: false },
        labels: { rotate: 0, style: { fontSize: '10px', colors: '#64748b' } },
      },
      yaxis: [
        { labels: { formatter: (v) => inrShort(v), style: { fontSize: '10px', colors: '#64748b' } } },
        { opposite: true, labels: { formatter: (v) => Math.round(v), style: { fontSize: '10px', colors: '#64748b' } } },
      ],
      grid: { borderColor: '#f1f5f9', strokeDashArray: 3, padding: { left: 4, right: 4, top: -14, bottom: 0 } },
      legend: { position: 'top', horizontalAlign: 'right', fontSize: '11px', markers: { radius: 12 }, offsetY: -6 },
      tooltip: { shared: true, y: [{ formatter: (v) => inr(v) }, { formatter: (v) => `${v} orders` }] },
    }),
    [trend]
  );

  const statuses = useMemo(() => data?.statusBreakdown || [], [data]);
  const statusTotal = statuses.reduce((a, s) => a + s.count, 0);
  const donutOptions = useMemo(
    () => ({
      chart: { type: 'donut', fontFamily: FONT, parentHeightOffset: 0 },
      labels: statuses.map((s) => s.label),
      colors: DONUT_COLORS,
      dataLabels: { enabled: false },
      stroke: { width: 2, colors: ['#fff'] },
      legend: { position: 'bottom', fontSize: '11px', markers: { radius: 12 }, itemMargin: { horizontal: 6, vertical: 2 }, formatter: (name, opts) => `${name}  ${opts.w.globals.series[opts.seriesIndex]}` },
      plotOptions: { pie: { donut: { size: '66%', labels: { show: true, name: { fontSize: '11px' }, value: { fontSize: '16px', fontWeight: 800 }, total: { show: true, label: '30 days', fontSize: '11px', formatter: () => statusTotal } } } } },
      tooltip: { y: { formatter: (v) => `${v} orders` } },
    }),
    [statuses, statusTotal]
  );

  const topMax = Math.max(1, ...(data?.topProducts || []).map((p) => p.units));

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: FONT, background: '#f8fafc', minHeight: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 25, fontWeight: 800, fontFamily: FONT, color: '#0f172a', lineHeight: 1.2 }}>Dashboard</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b', mt: 0.25, fontFamily: FONT }}>
            Store snapshot{data?.generatedAt ? ` · updated ${new Date(data.generatedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
          </Typography>
        </Box>
        <Tooltip title="Refresh" arrow>
          <IconButton onClick={() => load()} size="small" sx={{ background: '#fff', border: '1px solid #e2e8f0' }}>
            <MdRefresh size={20} />
          </IconButton>
        </Tooltip>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2.5, borderRadius: 1 }} />}
      {error && <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError('')}>{error}</Alert>}

      {data && (
        <>
          {/* KPI strip */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 3, mb: 3, '& > *': { minWidth: 0 } }}>
            {cards.map((c) => (
              <StatCard key={c.label} large {...c} />
            ))}
          </Box>

          {/* Trend + needs attention */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.7fr) minmax(0, 1fr)' }, gap: 3, mb: 3, alignItems: 'stretch', '& > *': { minWidth: 0 } }}>
            <Panel title="Sales & orders · last 30 days" accent="#6366f1" action="Sales report" onAction={go('/reports/sales-revenue')}>
              <ReactApexChart type="line" width="100%" height={320} options={trendOptions} series={[{ name: 'Sales', type: 'area', data: trend.map((t) => t.revenue) }, { name: 'Orders', type: 'column', data: trend.map((t) => t.orders) }]} />
            </Panel>

            <Panel title="Needs your attention" accent="#dc2626" action="Order report" onAction={go('/reports/orders')}>
              <Box sx={{ maxHeight: 340, overflowY: 'auto', pr: 0.5 }}>
                {data.attention.length === 0 ? (
                  <Box sx={{ minHeight: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#16a34a', gap: 1 }}>
                    <MdCheckCircle size={44} />
                    <Typography sx={{ fontWeight: 700, fontFamily: FONT }}>All clear — nothing needs action</Typography>
                  </Box>
                ) : (
                  data.attention.map((a) => (
                    <Box
                      key={a.key}
                      onClick={go(a.path)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, px: 1.5, borderRadius: '10px', cursor: 'pointer', borderLeft: `5px solid ${SEVERITY[a.severity]}`, mb: 1, background: '#f8fafc', '&:hover': { background: '#f1f5f9' } }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontFamily: FONT, lineHeight: 1.3, color: '#0f172a' }}>{a.label}</Typography>
                        {(a.note || a.amount) && (
                          <Typography sx={{ fontSize: 11.5, color: '#64748b', mt: 0.25, fontFamily: FONT }}>{a.note || inrShort(a.amount)}{a.note && a.amount ? ` · ${inrShort(a.amount)}` : ''}</Typography>
                        )}
                      </Box>
                      {a.key === 'sellerDue' ? (
                        <Chip size="small" label={inrShort(a.amount)} sx={pill('#dbeafe', '#1d4ed8')} />
                      ) : (
                        <Chip size="small" label={a.key === 'cod' ? `${a.count} · ${inrShort(a.amount)}` : a.count} sx={pill(`${SEVERITY[a.severity]}22`, SEVERITY[a.severity])} />
                      )}
                      <MdChevronRight size={16} color="#94a3b8" />
                    </Box>
                  ))
                )}
              </Box>
            </Panel>
          </Box>

          {/* Status + top products + low stock */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 3, mb: 3, alignItems: 'stretch', '& > *': { minWidth: 0 } }}>
            <Panel title="Order status · last 30 days" accent="#f59e0b" action="Orders" onAction={go('/ordermanagement?from=' + ymd(new Date(Date.now() - 29 * 86400000)))}>
              {statusTotal === 0 ? <Empty text="No orders in the last 30 days" /> : <ReactApexChart type="donut" width="100%" height={330} options={donutOptions} series={statuses.map((s) => s.count)} />}
            </Panel>

            <Panel title="Top selling · last 30 days" accent="#10b981" action="Product report" onAction={go('/reports/product-sales')}>
              {data.topProducts.length === 0 ? (
                <Empty text="No product sales yet" />
              ) : (
                data.topProducts.map((p, i) => (
                  <Box key={`${p.title}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderBottom: i < data.topProducts.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <Typography sx={{ width: 20, fontWeight: 800, color: '#94a3b8', fontSize: 12.5, fontFamily: FONT }}>{i + 1}</Typography>
                    <Avatar variant="rounded" src={p.image || undefined} sx={{ width: 44, height: 44, bgcolor: '#e2e8f0', color: '#475569', fontSize: 11.5, fontWeight: 700 }}>{(p.title || '?').charAt(0)}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700, fontFamily: FONT }}>{p.title}</Typography>
                      <LinearProgress variant="determinate" value={(p.units / topMax) * 100} sx={{ height: 7, borderRadius: 4, mt: 0.75, background: '#e2e8f0', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#10b981,#059669)', borderRadius: 3 } }} />
                    </Box>
                    <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 800, fontFamily: FONT }}>{p.units} sold</Typography>
                      <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{inrShort(p.revenue)}</Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Panel>

            <Panel title="Low & out of stock" accent="#ef4444" action="Inventory" onAction={go('/reports/inventory-report')} sx={{ gridColumn: { md: 'span 2', xl: 'span 1' } }}>
              {data.lowStock.length === 0 ? (
                <Empty text="All products are well stocked" />
              ) : (
                data.lowStock.map((p, i) => {
                  const pct = p.min > 0 ? Math.min((p.available / p.min) * 100, 100) : p.available > 0 ? 100 : 0;
                  const color = p.available <= 0 ? '#dc2626' : '#f59e0b';
                  return (
                    <Box key={`${p.title}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderBottom: i < data.lowStock.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <Avatar variant="rounded" src={p.image || undefined} sx={{ width: 44, height: 44, bgcolor: '#e2e8f0', color: '#475569', fontSize: 11.5, fontWeight: 700 }}>{(p.title || '?').charAt(0)}</Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700, fontFamily: FONT }}>{p.title}</Typography>
                        <LinearProgress variant="determinate" value={pct} sx={{ height: 7, borderRadius: 4, mt: 0.75, background: '#e2e8f0', '& .MuiLinearProgress-bar': { background: color, borderRadius: 3 } }} />
                      </Box>
                      <Chip size="small" label={p.available <= 0 ? 'Out of stock' : `${p.available} left`} sx={p.available <= 0 ? pill('#fee2e2', '#b91c1c') : pill('#fef3c7', '#92400e')} />
                    </Box>
                  );
                })
              )}
            </Panel>
          </Box>

          {/* Recent orders */}
          <Panel title="Recent orders" accent="#0ea5e9" action="All orders" onAction={go('/ordermanagement')}>
            <Box sx={{ overflowX: 'auto' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 12.5, '& th': { textAlign: 'left', fontWeight: 800, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.25, px: 2, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }, '& td': { py: 1.75, px: 2, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }, '& tr:last-child td': { borderBottom: 'none' } }}>
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Customer</th>
                    <th style={{ textAlign: 'center' }}>Items</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Placed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentOrders.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>No orders yet</td></tr>
                  )}
                  {data.recentOrders.map((o) => {
                    const [sb, sf] = ORDER_STATUS[o.status] || ['#f1f5f9', '#475569'];
                    const [pb, pf] = PAY_STATUS[o.paymentStatus] || ['#f1f5f9', '#475569'];
                    return (
                      <tr key={o.orderNumber}>
                        <td style={{ fontWeight: 800 }}>{o.orderNumber}</td>
                        <td>{o.customer}</td>
                        <td style={{ textAlign: 'center' }}>{o.items}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{inr(o.amount)}</td>
                        <td><Chip size="small" label={label(o.paymentStatus)} sx={pill(pb, pf)} /></td>
                        <td><Chip size="small" label={label(o.status)} sx={pill(sb, sf)} /></td>
                        <td style={{ textAlign: 'right', color: '#64748b' }}>{timeAgo(o.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Box>
            </Box>
          </Panel>
        </>
      )}
    </Box>
  );
};

export default AdminDashboard;
